<template>
  <div class="custom-color-picker">
    <el-color-picker
      v-model="colorValue"
      :predefine="predefineColors"
      popper-class="custom-color-picker-dropdown"
      @change="handleColorChange"
      format="hex"
    />
    <el-input
      v-model="displayValue"
      :placeholder="placeholder"
      size="default"
      class="color-input"
      @input="handleInputChange"
    />
  </div>
</template>

<script>
import {
  defineComponent, ref, watch, computed,
} from 'vue';

export default defineComponent({
  name: 'CustomColorPicker',
  props: {
    modelValue: {
      type: String,
      default: '#000000',
    },
    placeholder: {
      type: String,
      default: '#000000',
    },
    predefineColors: {
      type: Array,
      default: () => [
        '#000000',
        '#FFFFFF',
        '#409EFF',
        '#67C23A',
        '#E6A23C',
        '#F56C6C',
        '#909399',
      ],
    },
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const colorValue = ref(props.modelValue);

    // Convert rgba to hex
    const rgbaToHex = (rgba) => {
      // Handle hex format already
      if (rgba.startsWith('#')) {
        return rgba;
      }

      // Handle rgba format
      const rgbaMatch = rgba.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d*\.?\d+))?\)/);
      if (rgbaMatch) {
        const r = parseInt(rgbaMatch[1], 10);
        const g = parseInt(rgbaMatch[2], 10);
        const b = parseInt(rgbaMatch[3], 10);

        // Convert RGB to hex
        const toHex = (n) => {
          const hex = n.toString(16);
          return hex.length === 1 ? `0${hex}` : hex;
        };
        return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
      }

      return rgba;
    };

    // Display value in hex format
    const displayValue = computed({
      get: () => rgbaToHex(colorValue.value),
      set: (value) => {
        colorValue.value = value;
      },
    });

    // Watch for external changes to modelValue
    watch(() => props.modelValue, (newValue) => {
      colorValue.value = newValue;
    });

    // Watch for internal changes to colorValue
    watch(colorValue, (newValue) => {
      emit('update:modelValue', newValue);
    });

    // Handle color picker change
    const handleColorChange = (value) => {
      colorValue.value = value;
    };

    // Handle input change
    const handleInputChange = (value) => {
      colorValue.value = value;
    };

    return {
      colorValue,
      displayValue,
      handleColorChange,
      handleInputChange,
    };
  },
});
</script>

<style scoped>
.custom-color-picker {
  display: flex;
  align-items: center;
  gap: 12px;
}

.color-input {
  max-width: 150px;
}

:deep(.el-color-picker__trigger) {
  width: 40px;
  height: 40px;
  padding: 2px;
}

:deep(.el-color-picker__color) {
  border-radius: 4px;
}
</style>
